import { judgeAllNull, keepTwoNull } from '@/pages/IndustrialGreenMicBigScreen/utils';
import {
  V2MicrogridOverviewGetOperatingPowerCurvePostResponse,
  V2MicrogridOverviewGetPowerPredictPostResponse,
} from '@maxtropy/intelligent-dispath-apis-v2';
import dayjs from 'dayjs';
import * as echarts from 'echarts';

export const getChartOption = (chartData?: V2MicrogridOverviewGetOperatingPowerCurvePostResponse) => {
  // 光伏运行功率
  let pvValueData = (chartData?.list ?? []).map(item => [dayjs(item.timestamp).valueOf(), keepTwoNull(item.pvValue)]);
  // 市电运行功率
  let meterValueData = (chartData?.list ?? []).map(item => [
    dayjs(item.timestamp).valueOf(),
    keepTwoNull(item.meterValue),
  ]);
  // 风电运行功率
  let wpValueData = (chartData?.list ?? []).map(item => [dayjs(item.timestamp).valueOf(), keepTwoNull(item.wpValue)]);
  // 用电负荷
  let loadValueData = (chartData?.list ?? []).map(item => [
    dayjs(item.timestamp).valueOf(),
    keepTwoNull(item.loadValue),
  ]);
  // 用电负荷预测
  let loadPredictValueData = (chartData?.list ?? []).map(item => [
    dayjs(item.timestamp).valueOf(),
    keepTwoNull(item.loadPredictValue),
  ]);
  // 储能运行功率
  let bsaValueData = (chartData?.list ?? []).map(item => [dayjs(item.timestamp).valueOf(), keepTwoNull(item.bsaValue)]);

  // 判断是否数据都为null
  const legendData = [
    {
      name: '光伏',
      isAllNull: judgeAllNull((chartData?.list ?? []).map(item => item.pvValue)),
      color: '#16DD8E',
    },
    {
      name: '市电',
      isAllNull: judgeAllNull((chartData?.list ?? []).map(item => item.meterValue)),
      color: '#00ADFF',
    },
    {
      name: '风电',
      isAllNull: judgeAllNull((chartData?.list ?? []).map(item => item.wpValue)),
      color: '#52E7FF',
    },
    {
      name: '用电负荷',
      isAllNull: judgeAllNull((chartData?.list ?? []).map(item => item.loadValue)),
      color: '#FFCB47',
    },
    {
      name: '用电负荷预测',
      isAllNull: judgeAllNull((chartData?.list ?? []).map(item => item.loadPredictValue)),
      color: '#CE90D1',
    },
    {
      name: '储能',
      isAllNull: judgeAllNull((chartData?.list ?? []).map(item => item.bsaValue)),
      color: '#FF477B',
    },
  ].filter(c => !c.isAllNull);

  const seriesData = [
    {
      name: '光伏',
      type: 'line',
      data: pvValueData,
      symbol: 'circle',
      symbolSize: 6,
      isAllNull: judgeAllNull((chartData?.list ?? []).map(item => item.pvValue)),
    },
    {
      name: '市电',
      type: 'line',
      data: meterValueData,
      symbol: 'circle',
      symbolSize: 6,
      isAllNull: judgeAllNull((chartData?.list ?? []).map(item => item.meterValue)),
    },
    {
      name: '风电',
      type: 'line',
      data: wpValueData,
      symbol: 'circle',
      symbolSize: 6,
      isAllNull: judgeAllNull((chartData?.list ?? []).map(item => item.wpValue)),
    },
    {
      name: '用电负荷',
      type: 'line',
      data: loadValueData,
      symbol: 'circle',
      symbolSize: 6,
      isAllNull: judgeAllNull((chartData?.list ?? []).map(item => item.loadValue)),
    },
    {
      name: '用电负荷预测',
      type: 'line',
      data: loadPredictValueData,
      symbol: 'circle',
      symbolSize: 6,
      lineStyle: {
        width: 2,
        type: 'dashed', //'dotted'点型虚线 'solid'实线 'dashed'线性虚线
      },
      isAllNull: judgeAllNull((chartData?.list ?? []).map(item => item.loadPredictValue)),
    },
    {
      name: '储能',
      type: 'line',
      data: bsaValueData,
      symbol: 'circle',
      symbolSize: 6,
      isAllNull: judgeAllNull((chartData?.list ?? []).map(item => item.bsaValue)),
    },
  ].filter(c => !c.isAllNull);

  return {
    tooltip: {
      trigger: 'axis',
      backgroundColor: 'rgba(0,0,0,0.8)',
      borderColor: 'transparent',
      textStyle: {
        color: 'rgba(255,255,255,0.85)',
      },
      formatter: (items: any) => {
        let { axisValue } = items[0];
        let firstStr = dayjs(axisValue).format('YYYY-MM-DD HH:mm:ss');
        let otherStr = '';
        let str = '';
        items.forEach((item: any) => {
          const { seriesName, data, marker } = item;
          otherStr += `<div style="margin-top:8px"><span style="display:inline-block;margin-right:4px;border-radius:10px;width:10px;height:10px;">${marker}</span>${seriesName} : &nbsp;${
            data[1] || data[1] === 0 ? data[1] : '-'
          }kW</div>`;
          str = firstStr + otherStr;
        });
        return str;
      },
    },
    grid: {
      left: 10,
      right: 10,
      top: 50,
      bottom: 30,
      containLabel: true,
    },
    color: legendData.map(i => i.color),
    dataZoom: [
      {
        type: 'slider',
        bottom: 15,
        left: 40,
        right: 20,
        height: 10,
        textStyle: {
          fontSize: 10,
        },
        labelFormatter: (value: string) => {
          return dayjs(value).format('HH:mm');
        },
      },
    ],

    legend: {
      right: 10,
      textStyle: {
        color: 'rgba(255,255,255,0.85)',
      },
      inactiveColor: 'rgba(255,255,255,0.45)',
      itemWidth: 16,
      itemHeight: 4,
      data: legendData.map(item => ({
        name: item.name,
        icon:
          item.name === '用电负荷预测' ? 'path://M54 468h261v90H54zM378 468h261v90H378zM702 468h261v90H702z' : 'rect',
      })),
    },
    xAxis: [
      {
        type: 'time',
        axisLabel: {
          formatter: '{HH}:{mm}',
          textStyle: {
            color: 'rgba(255,255,255,.85)', //坐标的字体颜色
          },
          fontSize: 10,
        },
        axisPointer: {
          type: 'shadow',
        },
      },
    ],
    yAxis: [
      {
        type: 'value',
        name: 'kW',
        nameTextStyle: {
          color: 'rgba(255,255,255,0.85)',
        },
        axisLabel: {
          formatter: '{value}',
          textStyle: {
            color: 'rgba(255,255,255,.85)', //坐标的字体颜色
          },
        },
        splitLine: {
          show: true,
          lineStyle: {
            color: '#66686A',
          },
        },
      },
    ],
    series: seriesData,
  };
};

const areaColors = [
  new echarts.graphic.LinearGradient(0, 0, 0, 1, [
    { offset: 0, color: '#4A90E220' },
    { offset: 1, color: '#4A90E210' },
  ]),
  new echarts.graphic.LinearGradient(0, 0, 0, 1, [
    { offset: 0, color: '#FFCB4720' },
    { offset: 1, color: '#FFCB4710' },
  ]),
];

export const getModalChartOption = (data?: V2MicrogridOverviewGetPowerPredictPostResponse) => {
  // 实际值
  let actualData = (data?.list ?? []).map(item => [dayjs(item.timestamp).valueOf(), keepTwoNull(item.actualTimeValue)]);

  // 预测值
  let predictiveData = (data?.list ?? []).map(item => [
    dayjs(item.timestamp).valueOf(),
    keepTwoNull(item.predictValue),
  ]);

  const unit = 'kW';

  return {
    tooltip: {
      trigger: 'axis',
      backgroundColor: 'rgba(0,0,0,0.8)',
      borderColor: 'transparent',
      textStyle: {
        color: 'rgba(255,255,255,0.85)',
      },
      formatter: (items: any) => {
        let { axisValue } = items[0];
        let firstStr = dayjs(axisValue).format('YYYY-MM-DD HH:mm:ss');
        let otherStr = '';
        let str = '';

        items.forEach((item: any) => {
          const { seriesName, data, marker } = item;
          otherStr += `<div style="margin-top:8px"><span style="display:inline-block;margin-right:4px;border-radius:10px;width:10px;height:10px;">${marker}</span>${seriesName} : &nbsp;${
            data[1] || data[1] === 0 ? data[1] : '-'
          }${unit}</div>`;
          str = firstStr + otherStr;
        });

        return str;
      },
    },
    grid: {
      left: 10,
      right: 10,
      top: 50,
      bottom: 40,
      containLabel: true,
    },
    dataZoom: [
      {
        type: 'slider',
        bottom: 10,
        left: 25,
        right: 15,
        height: 20,
        textStyle: {
          fontSize: 10,
        },
        labelFormatter: (value: string) => {
          return dayjs(value).format('YYYY-MM-DD HH:mm:ss');
        },
      },
    ],
    legend: [
      {
        right: 90,
        itemGap: 18,
        itemWidth: 12,
        itemHeight: 12,
        textStyle: {
          color: 'rgba(255,255,255,0.85)',
        },
        inactiveColor: 'rgba(255,255,255,0.45)',
        data: [
          {
            name: '实际值',
            icon: 'rect',
          },
        ],
      },
      {
        right: 10,
        itemGap: 18,
        itemWidth: 12,
        itemHeight: 12,
        textStyle: {
          color: 'rgba(255,255,255,0.85)',
        },
        inactiveColor: 'rgba(255,255,255,0.45)',
        data: [
          {
            name: '预测值',
            icon: 'rect',
          },
        ],
      },
    ],
    xAxis: [
      {
        type: 'time',
        axisLabel: {
          textStyle: {
            color: 'var(--mx-text-base-color)', //坐标的字体颜色
          },
          fontSize: 10,
        },
        axisPointer: {
          type: 'shadow',
        },
        axisLine: {
          show: true, // 是否显示轴线
          lineStyle: {
            color: 'rgba(255,255,255,0.3)',
          },
        },
      },
    ],
    yAxis: [
      {
        type: 'value',
        name: unit,
        axisLabel: {
          formatter: '{value}',
          textStyle: {
            color: 'var(--mx-text-base-color)', //坐标的字体颜色
          },
        },
        splitLine: {
          show: true,
          lineStyle: {
            color: 'rgba(255,255,255,0.3)',
          },
        },
      },
    ],
    series: [
      {
        name: '实际值',
        type: 'line',
        data: actualData,
        connectNulls: true,
        smooth: true,
        symbol: 'circle',
        symbolSize: 6,
        lineStyle: {
          width: 2,
          type: 'solid', //'dotted'点型虚线 'solid'实线 'dashed'线性虚线
        },
        areaStyle: {
          color: areaColors[0],
        },
      },
      {
        name: '预测值',
        type: 'line',
        data: predictiveData,
        connectNulls: true,
        smooth: true,
        symbol: 'circle',
        symbolSize: 6,
        lineStyle: {
          width: 2,
          type: 'solid', //'dotted'点型虚线 'solid'实线 'dashed'线性虚线
        },
        areaStyle: {
          color: areaColors[1],
        },
      },
    ],
  };
};
